import { Assets, type AssetsBundle } from 'pixi.js';
import { getAssetsToPreload } from '../libs/getAssetsToPreload';
import type { LayoutConstructor } from '../libs/getAssetsToPreload';
import { LoadingView } from './LoadingView';
import { waitForMs } from '../utils/async-utils';
import { getLayoutClassByPath, normalizeLayoutPath } from '../libs/layouts/runtime/layoutModules';
import type { RawLayoutData } from '../libs/layouts/types/RawLayout';

export async function preloadLayoutRootBundles(rootBundlesData: Record<string, AssetsBundle['assets']>): Promise<void> {
  const bundlesPromises = Object.entries(rootBundlesData).map(async ([key, bundleAssets]) => {
    if (Assets.cache.has(key)) return;
    if (!Assets.resolver.resolveBundle(key)) {
      Assets.addBundle(key, bundleAssets);
    }
    await Assets.loadBundle(key);
  });

  await Promise.all(bundlesPromises);
}

function isRawLayoutData(input: unknown): input is RawLayoutData {
  if (!input || typeof input !== 'object') return false;
  const raw = input as Record<string, unknown>;
  return (
    Array.isArray(raw.list) &&
    typeof raw.x === 'number' &&
    typeof raw.y === 'number' &&
    typeof raw.width === 'number' &&
    typeof raw.height === 'number' &&
    typeof raw.version === 'string'
  );
}

function isCtorWithRaw(input: unknown): input is LayoutConstructor {
  if (!input) return false;
  const maybe = input as { raw?: unknown };
  // static raw lives on the constructor function object
  return !!(maybe && typeof maybe === 'function' && 'raw' in maybe && isRawLayoutData((maybe as { raw: unknown }).raw));
}

function coerceToLayoutConstructor(item: unknown): LayoutConstructor | null {
  // Case 1: direct constructor (class) with static raw
  if (isCtorWithRaw(item)) return item;

  // Case 2: string path to a local layout inside /layouts
  if (typeof item === 'string') {
    const normalized = normalizeLayoutPath(item);
    const ctor = getLayoutClassByPath(normalized);
    if (ctor && isCtorWithRaw(ctor)) return ctor as unknown as LayoutConstructor;
    // Not found – ignore
    return null;
  }

  // Case 3: object with `raw` property
  if (item && typeof item === 'object' && 'raw' in (item as Record<string, unknown>)) {
    const { raw } = item as { raw: unknown };
    if (isRawLayoutData(raw)) {
      // Coerce to LayoutConstructor-like shape
      return { raw } as unknown as LayoutConstructor;
    }
  }

  // Case 4: raw data itself
  if (isRawLayoutData(item)) {
    return { raw: item } as unknown as LayoutConstructor;
  }

  return null;
}

export async function preloadLayoutsAssets(layouts: unknown[], showLoadingView = false): Promise<boolean> {
  const normalizedCtors = layouts
    .map((l) => coerceToLayoutConstructor(l))
    .filter((v): v is LayoutConstructor => v !== null);

  const { assetsToPreload, bundles } = getAssetsToPreload(normalizedCtors);

  if (assetsToPreload.length === 0 && Object.values(bundles).length === 0) return true;

  if (showLoadingView) void LoadingView.getInstance().show();

  let loaded = false;

  const assetLoadingPromise = Promise.all([
    ...assetsToPreload.map((assetName) => Assets.load(assetName)),
    preloadLayoutRootBundles(bundles),
  ]);

  await Promise.race([
    waitForMs(10000),
    assetLoadingPromise.then(
      () => {
        loaded = true;
      },
      () => {},
    ),
  ]);

  if (showLoadingView) await LoadingView.getInstance().hide();

  return loaded;
}
